import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Globe, Search, Zap, CheckCircle, ArrowRight, Send, TrendingUp, AlertCircle, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CursorGlow from '../components/CursorGlow';
import ScrollProgress from '../components/ScrollProgress';

const WebAnalysis = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    website: '',
    name: '',
    email: '',
    phone: ''
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsAnalyzing(true);
    
    // Simulate analysis
    setTimeout(() => {
      setIsAnalyzing(false);
      console.log('Web analysis form submitted:', formData);
      toast({
        title: "Analiz Talebiniz Alındı! 🔍",
        description: "24 saat içinde detaylı SEO raporunuzu e-posta adresinize göndereceğiz.",
      });
      
      // Redirect to thank you page
      setTimeout(() => {
        navigate('/tesekkurler?type=analysis');
      }, 1500);
    }, 2000);
  };

  const analysisFeatures = [
    {
      icon: Search,
      title: 'SEO Performans Analizi',
      description: 'Anahtar kelime sıralamaları, meta tag optimizasyonu ve içerik kalitesi değerlendirmesi'
    },
    {
      icon: Zap,
      title: 'Site Hızı Testi',
      description: 'Sayfa yüklenme süreleri, Core Web Vitals metrikleri ve performans önerileri'
    },
    {
      icon: Globe,
      title: 'Teknik SEO Denetimi',
      description: 'Crawlability, indexability, sitemap ve robots.txt analizi'
    },
    {
      icon: TrendingUp,
      title: 'Rakip Analizi',
      description: 'Sektörünüzdeki rakiplerle karşılaştırmalı performans değerlendirmesi'
    }
  ];

  const reportIncludes = [
    'Genel SEO skoru ve değerlendirme',
    'Teknik SEO sorunları listesi',
    'Sayfa hızı analizi ve öneriler',
    'Mobil uyumluluk raporu',
    'Anahtar kelime fırsatları',
    'Backlink profil analizi',
    'Rakip karşılaştırması',
    'Öncelikli aksiyon planı'
  ];

  const faqItems = [
    {
      question: 'Analiz ne kadar sürede hazır olur?',
      answer: 'Analiz talebiniz alındıktan sonra 24 saat içinde detaylı raporunuz e-posta adresinize gönderilir.'
    },
    {
      question: 'Analiz gerçekten ücretsiz mi?',
      answer: 'Evet, ilk SEO analizi tamamen ücretsizdir. Herhangi bir ödeme veya taahhüt gerektirmez.'
    },
    {
      question: 'Hangi bilgilere ihtiyacınız var?',
      answer: 'Sadece web sitenizin URL\'si ve iletişim bilgileriniz yeterli. Herhangi bir erişim bilgisine ihtiyacımız yok.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-teal-500/20 rounded-full blur-3xl bottom-0 left-1/4 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
                <BarChart3 className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-medium">Ücretsiz Web Analizi</span>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Web Sitenizin
                <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> SEO Skorunu </span>
                Öğrenin
              </h1>

              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                Ücretsiz ve kapsamlı SEO analizi ile web sitenizin güçlü ve zayıf yönlerini keşfedin. 
                Dijital görünürlüğünüzü artırmak için uzman önerileri alın.
              </p>

              <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400" />
                  <span>100% Ücretsiz</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400" />
                  <span>24 Saat İçinde Rapor</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400" />
                  <span>Uzman Değerlendirmesi</span>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-slate-800/60 backdrop-blur-xl border border-cyan-500/30 rounded-3xl p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">
                  Ücretsiz Analiz Talep Et
                </h2>
                <p className="text-gray-400 text-sm">
                  Formu doldurun, 24 saat içinde raporunuz hazır olsun
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="website" className="text-gray-300 mb-2 block">Web Siteniz *</Label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      id="website"
                      required
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl pl-12 focus:border-cyan-500"
                      placeholder="https://www.siteniz.com"
                      data-testid="analysis-website-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="name" className="text-gray-300 mb-2 block">Adınız Soyadınız *</Label>
                  <Input
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl focus:border-cyan-500"
                    placeholder="Ahmet Yılmaz"
                    data-testid="analysis-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-gray-300 mb-2 block">E-posta *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl focus:border-cyan-500"
                    placeholder="ahmet@sirket.com"
                    data-testid="analysis-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-gray-300 mb-2 block">Telefon</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-slate-900/50 border-slate-600 text-white h-12 rounded-xl focus:border-cyan-500"
                    placeholder="+90 XXX XXX XX XX"
                    data-testid="analysis-phone-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isAnalyzing}
                  className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white py-6 text-lg font-semibold rounded-xl transition-all hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="analysis-submit-button"
                >
                  {isAnalyzing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Analiz Ediliyor...
                    </>
                  ) : (
                    <>
                      Ücretsiz Analiz Başlat
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-center text-xs text-gray-500">
                  Bilgileriniz gizli tutulur ve üçüncü taraflarla paylaşılmaz.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Analizde Neler
              <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Var?</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Kapsamlı SEO analizimiz ile web sitenizin tüm yönlerini değerlendiriyoruz
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {analysisFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-cyan-500/50 transition-all hover:-translate-y-2"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Report Contents */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Raporunuzda
                <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Neler Olacak?</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Uzman ekibimiz tarafından hazırlanan detaylı rapor ile web sitenizin tam bir röntgenini çekiyoruz.
              </p>

              <div className="space-y-4">
                {reportIncludes.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />
                    </div>
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <FileText className="w-8 h-8 text-cyan-400" />
                  <div>
                    <h3 className="text-white font-bold">SEO Analiz Raporu</h3>
                    <p className="text-gray-500 text-sm">Örnek Rapor Önizlemesi</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Genel SEO Skoru</span>
                    <span className="text-2xl font-bold text-cyan-400">72/100</span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full w-[72%] bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full"></div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="bg-slate-900/50 rounded-xl p-4">
                      <div className="text-red-400 text-2xl font-bold">12</div>
                      <div className="text-gray-500 text-sm">Kritik Sorun</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-xl p-4">
                      <div className="text-yellow-400 text-2xl font-bold">23</div>
                      <div className="text-gray-500 text-sm">Uyarı</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-xl p-4">
                      <div className="text-green-400 text-2xl font-bold">45</div>
                      <div className="text-gray-500 text-sm">Başarılı</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-xl p-4">
                      <div className="text-blue-400 text-2xl font-bold">8</div>
                      <div className="text-gray-500 text-sm">Fırsat</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -z-10 -inset-4 bg-gradient-to-r from-cyan-500/20 to-teal-500/20 rounded-3xl blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-12 text-center">
            Sıkça Sorulan
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Sorular</span>
          </h2>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <AlertCircle className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">{item.question}</h3>
                    <p className="text-gray-400">{item.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Hemen Başlayın
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Ücretsiz SEO analizi ile dijital varlığınızı güçlendirin. 
            Raporunuz 24 saat içinde hazır.
          </p>
          <Button 
            onClick={() => document.getElementById('website')?.focus()}
            className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-8 py-6 text-lg rounded-xl"
          >
            Ücretsiz Analiz İste
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WebAnalysis;
